import { useMutation } from "react-query";
import { useRouter } from "../../useRouter";
import { useRecoilState } from "recoil";
import {
  hasSiteAtom,
  isShowSideBarAtom,
  onceLoginUUIDAtom,
  productKeyAtom,
  userInfoAtom,
} from "src/atoms";
import {
  getProductFromSessionStorage,
  setProductToSessionStorage,
  setUserInfoToLocalStorage,
} from "src/utils/storage/auth";
import { notifyError, notifySuccess } from "src/components/Common/Toast";
import { useApiError } from "../../useApiError";
import { login as loginReq, onceLogin } from "src/service/sessions";
import { Dispatch, SetStateAction } from "react";
import { IUserInfo } from "src/service/types/common.type";
import { useSearchParams } from "react-router-dom";
import { useLogout } from "./useLogout";
import { AxiosError } from "axios";
import {
  ProductKey,
  ProductKeyList,
  WRAPSODY_CLOUD_URL,
  WRAPSODY_ECO_CLOUD_URL,
} from "src/config/config";
import { getCashUserStatus, getServiceUnregisterBill } from "src/service/pay";
import { getMyServiceList } from "src/service/console";
import { getCreatedServiceList } from "src/service/cloud";
import { UseFormSetError } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface IUseLogInProps {
  setInvalidUser?: Dispatch<SetStateAction<boolean>>;
  setError?: UseFormSetError<any>;
  isOnceLogin?: boolean;
  hasSite?: string | null; // 사이트 있을시 사이트명 없으면 null
  type?: "COMMON" | "PROMOTION_CARD";
}
export function useLogIn({
  setInvalidUser,
  setError,
  isOnceLogin = false,
  type = "COMMON",
}: IUseLogInProps) {
  const { t } = useTranslation();
  const [userInfo, setUserInfo] = useRecoilState(userInfoAtom);
  const {
    goConsole,
    goSiteType,
    goServiceList,
    goBill,
    routeTo,
    productReload,
    parentRouteTo,
  } = useRouter();
  const [searchParams, setSearchParams] = useSearchParams();
  const [producyKey, serProductKey] = useRecoilState(productKeyAtom);
  const [onceLoginUUID, setOnceLoginUUID] = useRecoilState(onceLoginUUIDAtom);
  const [isShowSideBar, setShowSideBar] = useRecoilState(isShowSideBarAtom);
  const [hasSite, setHasSite] = useRecoilState(hasSiteAtom);
  // const { unregisterPayList } = useGetPayUnregisterBill({});
  // const { unregisterPromotionList } = useGetPromotionUnregisterBill({});
  const { mutateLogout } = useLogout();
  const { handleError } = useApiError({
    500: {
      INVALID_USER_ID_PWD: () => {
        if (setError) {
          setError("password", {
            message: t("logIn.invalidUser"),
          });
        }
        notifyError(t("logIn.invalidUser"));
        if (setInvalidUser) setInvalidUser(true);
      },
      ALREADY_LOGIN: (e: AxiosError) => {
        console.log("ALREADY_LOGIN e", e);
        // 로그아웃 후 이전요청 재개
        // logout({}).then(() => e.request(e.config));
        // goConsole();
      },
      default: (err: any) => {
        notifyError(err?.response?.data?.errorMessage ?? "error");
      },
    },
    400: {
      default: (err: any) => {
        if (isOnceLogin) {
          if (onceLoginUUID !== "") {
            if (producyKey === "WRAPSODY") {
              parentRouteTo({ path: WRAPSODY_CLOUD_URL });
            } else if (producyKey === "WRAPSODY_ECO") {
              parentRouteTo({ path: WRAPSODY_ECO_CLOUD_URL });
            } else {
              goBill();
            }
          } else {
            notifyError("Expired UUID");
          }
        } else {
          notifyError("400 Bad Request...");
        }
      },
    },
  });
  const { mutateAsync: mutateLogin } = useMutation("login", loginReq, {
    onSuccess: async (data) => {
      // 로그인 성공시 유저정보 로컬저장소에 저장 후 /console로 이동
      const productKey = data?.userInfo?.loginType as ProductKey;
      const userInfo = {
        userInfo: data?.userInfo,
        loginUnixTime: data?.loginUnixTime,
        loginTimeIso8601: data?.loginTimeIso8601,
        loginIp: data?.loginIp,
      };
      setUserInfoToLocalStorage(JSON.stringify(userInfo));
      if (!getProductFromSessionStorage()) {
        setProductToSessionStorage(
          ProductKeyList.includes(productKey) ? productKey : "WRAPSODY"
        );
        serProductKey(
          ProductKeyList.includes(productKey) ? productKey : "WRAPSODY"
        );
      }
      setUserInfo(data?.userInfo as IUserInfo);

      notifySuccess(t("logIn.successMsg"));
      if (type === "COMMON") {
        // 보유한 클라우드 서비스가 있으면 제품 선택 화면으로 먼저 이동 (OCI 제외)
        const isOciProduct = ProductKeyList.includes(productKey);
        const createdServiceList = isOciProduct
          ? []
          : await getCreatedServiceList().catch(() => []);
        if (createdServiceList.length > 0) goServiceList();
        else goConsole();
      }
    },
    onError: handleError,
  });
  const { mutateAsync: mutateOnceLogin } = useMutation("onceLogin", onceLogin, {
    onSuccess: async (data) => {
      // 로그인 성공시 유저정보 로컬저장소에 저장 후 /console로 이동
      // console.log("onceLogin 성공", data);
      const productKey = data?.userInfo?.loginType as ProductKey;
      const userInfo = {
        userInfo: data?.userInfo,
        loginUnixTime: data?.loginUnixTime,
        loginTimeIso8601: data?.loginTimeIso8601,
        loginIp: data?.loginIp,
      };
      const hasSite = searchParams.get("site") ?? null;
      setHasSite(hasSite);
      // console.log("hasSite", hasSite);
      // console.log('searchParams.get("site")', searchParams.get("site"));
      setUserInfoToLocalStorage(JSON.stringify(userInfo));
      if (!getProductFromSessionStorage()) {
        setProductToSessionStorage(
          ProductKeyList.includes(productKey) ? productKey : "WRAPSODY"
        );
        serProductKey(
          ProductKeyList.includes(productKey) ? productKey : "WRAPSODY"
        );
      }
      setUserInfo(data?.userInfo as IUserInfo);

      // notifySuccess(t("logIn.title"));
      if (hasSite) {
        // 사이트 있는경우
        if (productKey === "WRAPSODY_ECO") {
          const serviceList = await getMyServiceList({ product: productKey });
          if (searchParams.get("site") === "My Space") {
            if (serviceList?.length > 0) {
              goBill();
            } else {
              goSiteType();
            }
          } else {
            goBill();
          }
        } else {
          if (searchParams.get("site")) {
            setShowSideBar(true);
          } else {
            setShowSideBar(false);
          }
          goConsole();
        }
      } else {
        // 없는 경우
        if (productKey === "WRAPSODY_ECO") {
          // +버튼 클릭
          const cashStatus = await getCashUserStatus({
            product: [productKey],
          });
          // 현금결제 요청의 경우 콘솔로 보내지 않음 (2026-04-27)
          // if (["WAIT", "APPLY"].includes(cashStatus.status)) {
          //   // 현금결제 요청 상태인 경우
          //   goConsole();
          // } else {
          //   goSiteType();
          // }
          goSiteType();
        } else {
          const unregisterPay = await getServiceUnregisterBill({
            billType: "PAY",
            product: productKey,
          });
          const cashStatus = await getCashUserStatus({
            product: [productKey],
          });
          if (
            ["WAIT", "APPLY"].includes(cashStatus.status) ||
            unregisterPay?.length !== 0
          ) {
            // 현금결제 요청 상태인 경우
            goConsole();
          } else {
            goSiteType();
          }
        }
      }
    },
    onError: handleError,
  });

  return { mutateLogin, mutateOnceLogin };
}
